import React, { useState } from 'react';
import axios from 'axios';
import { EyeIcon, EyeOffIcon } from '@heroicons/react/solid';
import { BASELINE } from '../util';
import { useNavigate } from 'react-router-dom';

const ForgotPasswordForm = () => {
    // 눈 추적 관련 smile state (login과 동일하게 사용)
    const [smile, setIsSmiling] = useState(false);
    const navigate = useNavigate();

    // 비밀번호 표시 토글
    const [showPassword, setShowPassword] = useState(false);
    const togglePasswordVisibility = () => setShowPassword(!showPassword);

    // 단계 구분 및 인증코드 관련 state
    const [codeSent, setCodeSent] = useState(false);
    const [sentCode, setSentCode] = useState('');

    // 폼 입력 데이터
    const [formData, setFormData] = useState({
        email: '',
        verificationCode: '',
        newPassword: '',
        confirmPassword: ''
    });

    // 안내 메시지
    const [message, setMessage] = useState('');

    // 입력값 변경 핸들러
    const handleChange = (e) => {
        setFormData(prev => ({
            ...prev,
            [e.target.name]: e.target.value,
        }));
    };

    // 6자리 무작위 코드 생성 함수
    const generateRandomCode = () => {
        return Math.floor(100000 + Math.random() * 900000).toString();
    };

    // (1단계) 인증코드 전송: 이메일 입력 후 버튼 클릭 시
    const handleSendCode = async (e) => {
        e.preventDefault();
        setMessage('');
        try {
            const randomCode = generateRandomCode();
            setSentCode(randomCode);
            await axios.post(BASELINE + 'sendVerificationEmail', {
                email: formData.email,
                code: randomCode,
            });
            setCodeSent(true);
            setMessage('A verification code has been sent to your email. Please check your inbox.');
        } catch (error) {
            setMessage(error.response?.data?.message || 'An error has occurred. Please try again.');
        }
    };

    // (2단계) 비밀번호 재설정: 인증코드와 새 비밀번호 입력 후 버튼 클릭 시
    const handleResetPassword = async (e) => {
        e.preventDefault();

        // 클라이언트측 인증코드 검증
        if (formData.verificationCode !== sentCode) {
            window.alert('The verification code you entered is incorrect.');
            return;
        }

        // 새 비밀번호와 확인 비밀번호 일치 검증
        if (formData.newPassword !== formData.confirmPassword) {
            window.alert('The new password and confirmation password do not match.');
            return;
        }

        try {
            const response = await axios.post(BASELINE + 'user/update/password', {
                email: formData.email,
                verificationCode: formData.verificationCode,
                newPassword: formData.newPassword,
            });
            // 서버에서 항상 200 상태로 반환하고 complete: true/false로 상태 전달
            const data = response.data;
            window.alert(data.message);
            if (data.complete) {
                navigate("/");
            }
        } catch (error) {
            window.alert(error.response?.data?.message || 'An error occurred. Please try again.');
        }
    };

    return (
        // 로그인 컴포넌트와 동일한 레이아웃: 중앙 정렬, 배경 색상 등
        <div className="flex flex-col justify-center items-center min-h-screen bg-gray-100 px-4">
            {/* 로그인 컴포넌트와 동일한 흰색 카드 박스 */}
            <div className="w-full max-w-md border bg-white p-6 sm:p-8 lg:p-10 rounded-md shadow-md text-[#232323]">
                {/* EyeTrackingHeader 컴포넌트로 눈 추적 헤더 (smile prop 포함) */}
                <div className="flex flex-col items-center mt-10">
                    <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                        <a href="https://p-vault.com/dashboard/login" className="flex">
                            <img src="/dashboard/logoonly.png" alt="" />
                        </a>
                    </h2>
                </div>
                {/* 페이지 설명 텍스트 */}
                <p className="mb-4 text-gray-500 text-[15px] text-center">
                    {codeSent
                        ? "Enter the verification code and set your new password."
                        : "Reset your password to continue to P-vault."}
                </p>

                {/* 인증코드 전송 단계와 비밀번호 재설정 단계 구분 */}
                {!codeSent ? (
                    <form onSubmit={handleSendCode}>
                        <input
                            type="email"
                            name="email"
                            placeholder="Email address*"
                            value={formData.email}
                            onChange={handleChange}
                            className="mb-4 w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                        />
                        <button
                            type="submit"
                            className="w-full mt-5 py-2 text-white bg-teal-500 rounded hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 transition-colors"
                            onMouseEnter={() => setIsSmiling(true)}
                            onMouseLeave={() => setIsSmiling(false)}
                        >
                            Send Verification Code
                        </button>
                    </form>
                ) : (
                    <form onSubmit={handleResetPassword}>
                        <input
                            type="text"
                            name="verificationCode"
                            placeholder="Verification Code*"
                            value={formData.verificationCode}
                            onChange={handleChange}
                            className="mb-4 w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                        />

                        <div className="relative mb-4">
                            <input
                                type={showPassword ? "text" : "password"}
                                name="newPassword"
                                placeholder="New Password*"
                                value={formData.newPassword}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
                                required
                            />
                            <span
                                className="absolute right-3 top-2.5 text-gray-400 cursor-pointer"
                                onClick={togglePasswordVisibility}
                            >
                                {showPassword ? <EyeOffIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                            </span>
                        </div>

                        <input
                            type={showPassword ? "text" : "password"}
                            name="confirmPassword"
                            placeholder="Confirm Password*"
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            className="mb-4 w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                        />

                        <button
                            type="submit"
                            className="w-full mt-5 py-2 text-white bg-teal-500 rounded hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 transition-colors"
                            onMouseEnter={() => setIsSmiling(true)}
                            onMouseLeave={() => setIsSmiling(false)}
                        >
                            Reset Password
                        </button>
                    </form>
                )}

                {/* 안내 메시지 */}
                {message && <p className="mt-4 text-green-600 text-center">{message}</p>}
            </div>
        </div>
    );
};

export default ForgotPasswordForm;
